// Bottom-of-sidebar user picker.
//
// Shows WHO you're signed in as (avatar initials + email, falling back to
// the user id) and, on click, a small popup whose only item is "Sign out".
// Sign-out reuses the existing `/logout` route (LogoutRedirect → POST
// /auth/logout → cookie clear → top-level nav to the Cognito logout URL) —
// this component only adds the identity display + the menu affordance the
// bare link lacked.
//
// `whoAmI` is already fetched + cached by `AuthGate` before any authed route
// (and therefore this component) renders, so the live query is a cache read.
//
// **Test / Storybook seam** (same shape as PendingApprovalsBadge): the
// `identity` prop bypasses the tRPC query entirely, so the unit test renders
// the View without needing a tRPC Provider. The hook is only invoked in the
// `Live` branch.

import { useEffect, useRef, useState } from 'react';

import { usePreviewFeatures } from '../prefs/index.js';
import { trpc } from '../trpc-client.js';

export interface UserIdentity {
  readonly userId: string;
  readonly email: string | null;
  /** Display name (given_name + family_name) when the token carries it. */
  readonly name?: string | null;
}

/**
 * Preview-features props threaded into the pure `UserMenuView`. Kept as a
 * separate seam (NOT read from the hook inside the View) so the View stays
 * provider-free and unit-testable — only `UserMenuLive` calls the hook.
 */
export interface UserMenuPreviewProps {
  /** Whether the "Show preview features" toggle is currently on. */
  readonly previewEnabled: boolean;
  /** Flip the preview-features flag. Keeps the menu open (APG checkbox-item). */
  readonly onTogglePreview: () => void;
}

export interface UserMenuProps extends Partial<UserMenuPreviewProps> {
  /** Identity override — bypasses the tRPC query for tests / storybook. */
  readonly identity?: UserIdentity;
}

/**
 * Two-letter avatar initials, uppercased. PREFERS the proper name — first
 * letter of the first + last name parts ("David Broadfoot" → "DB"); a
 * single-word name → its first two letters. Falls back to the first two
 * letters of the email local-part, then the user id.
 */
export function userInitials(
  name: string | null | undefined,
  email: string | null,
  userId: string,
): string {
  if (name !== null && name !== undefined) {
    const tokens = name
      .trim()
      .split(/\s+/)
      .filter((t) => t.length > 0);
    if (tokens.length >= 2) {
      const first = tokens[0] ?? '';
      const last = tokens[tokens.length - 1] ?? '';
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (tokens.length === 1) {
      return ((tokens[0] ?? '').slice(0, 2) || '?').toUpperCase();
    }
  }
  const source = email ?? userId;
  const localPart = source.split('@')[0] ?? source;
  const letters = localPart.replace(/[^a-zA-Z0-9]/g, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
}

export function UserMenu(props: UserMenuProps): React.ReactElement | null {
  if (props.identity !== undefined) {
    // Provider-free path (tests / storybook): pass the preview props straight
    // through, defaulting to a no-op disabled toggle when the caller omits them
    // so the View never has to reach for the hook.
    return (
      <UserMenuView
        identity={props.identity}
        previewEnabled={props.previewEnabled ?? false}
        onTogglePreview={props.onTogglePreview ?? (() => {})}
      />
    );
  }
  return <UserMenuLive />;
}

function UserMenuLive(): React.ReactElement | null {
  const { data } = trpc.whoAmI.useQuery(undefined, { staleTime: Number.POSITIVE_INFINITY });
  const { enabled, toggle } = usePreviewFeatures();
  // AuthGate gates rendering on whoAmI success, so `data` is present in
  // practice; the guard keeps this safe if it ever renders without it.
  if (data === undefined) return null;
  return (
    <UserMenuView
      identity={{ userId: data.userId, email: data.email, name: data.name }}
      previewEnabled={enabled}
      onTogglePreview={toggle}
    />
  );
}

function UserMenuView({
  identity,
  previewEnabled,
  onTogglePreview,
}: UserMenuPreviewProps & { identity: UserIdentity }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const label = identity.name ?? identity.email ?? identity.userId;

  return (
    <div className="user-menu" ref={rootRef} data-testid="user-menu">
      {open && (
        <div className="user-menu-popup" role="menu" aria-label="Account">
          {/* Preview-features toggle — APG `menuitemcheckbox`: activating it
              flips the flag and KEEPS the menu open (distinct from the plain
              "Sign out" menuitem, which navigates + closes). Cosmetic
              nav-gating only — never a security boundary. */}
          <button
            type="button"
            className="user-menu-item user-menu-toggle"
            role="menuitemcheckbox"
            aria-checked={previewEnabled}
            onClick={() => onTogglePreview()}
          >
            <span className="user-menu-toggle-check" aria-hidden="true">
              {previewEnabled ? '✓' : ''}
            </span>
            <span className="user-menu-toggle-label">Show preview features</span>
          </button>
          <a className="user-menu-item" role="menuitem" href="/logout">
            Sign out
          </a>
        </div>
      )}
      <button
        type="button"
        className="user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${label}`}
        onClick={() => setOpen((previous) => !previous)}
      >
        <span className="user-menu-avatar" aria-hidden="true">
          {userInitials(identity.name, identity.email, identity.userId)}
        </span>
        <span className="user-menu-label" title={label}>
          {label}
        </span>
        <span className="user-menu-chevron" aria-hidden="true">
          {open ? '▾' : '▴'}
        </span>
      </button>
    </div>
  );
}
